import { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

import { getTablesPaginated } from '../../../../redux/slices/tablesSlice';

const toActionEntity = (row: any): ActionEntity => ({ type: 'table', id: row.table_id, zoneName: row.zone_name, tableName: row.table_name });

const ListTables = () => {
  const dispatch = useAppDispatch();

  const { items: tables, status, error, loading, config, filtersConfig, pagination } = useAppSelector((state) => state.tables);

  const isLoading = loading?.getTablesPaginated ?? false;

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'tables';
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    if (status === null) {
      dispatch(getTablesPaginated({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig: config,
    fetchData: getTablesPaginated,
    getFormattedRow: (row: any) => row,
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['editTable', 'editRows', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(tables || []), [tables]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: false,
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const toolbarActions = getActions({ entities: [], refresh });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(config ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={<ActionButton actions={toolbarActions} action="createTable" variant="primary" />}
    />
  );
};

export default ListTables;
